import { useState, useMemo, type ReactNode } from 'react';
import { MagnifyingGlassIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/16/solid';
import { getAllColumns } from '../../../../../../components/Table/columns/index';
import { ColumnDefinition } from '../../../../../../components/Table/columns/shared/column-types';

const CATEGORY_ORDER = ['GDA', 'VDA', 'Relation', 'DDA', 'Disease', 'Gene', 'Variant', 'Chemical'];

function getHeaderText(header: unknown): string {
  if (header == null) return '';
  if (typeof header === 'string') return header.trim();
  if (typeof header === 'number') return String(header);
  if (Array.isArray(header)) return header.map(getHeaderText).join('').trim();
  if (typeof header === 'object' && header !== null && 'props' in header) {
    const el = header as { props?: { children?: ReactNode } };
    if (el.props?.children != null) return getHeaderText(el.props.children);
  }
  return '';
}

const COLUMN_LABEL_FALLBACK: Record<string, string> = {
  score: 'Score',
  sourceScore: 'My Score',
};

export interface SidebarColumnsProps {
  view: string;
  tab: string;
  isEvidence?: boolean;
  columnVisibilityModel: Record<string, boolean>;
  setColumnVisibilityModel: (updater: (prev: Record<string, boolean>) => Record<string, boolean>) => void;
}

type ColumnDisplay = { key: string; header: string; category?: string };

function buildDisplayFragment(
  allColumns: ColumnDefinition[],
  columnVisibilityModel: Record<string, boolean>,
  view: string,
  tab: string,
  isEvidence: boolean,
): Record<string, ColumnDisplay> {
  const acc: Record<string, ColumnDisplay> = {};
  Object.keys(columnVisibilityModel).forEach(key => {
    const column: ColumnDefinition | undefined = allColumns.find(col => col.name === key);
    if (column && column.header) {
      let shouldShow = true;
      if (typeof column.show === 'function') {
        shouldShow = column.show({ tab, view, isEvidence });
      } else if (column.show !== undefined) {
        shouldShow = column.show;
      }
      if (shouldShow) {
        const headerLabel =
          getHeaderText(column.header) ||
          COLUMN_LABEL_FALLBACK[key] ||
          (column.name ?? key).replace(/([A-Z])/g, ' $1').trim();
        acc[key] = {
          key,
          header: headerLabel,
          category: column.category,
        };
      }
    }
  });
  return acc;
}

function groupByCategory(
  displayFragment: Record<string, ColumnDisplay>,
): { category: string; columns: ColumnDisplay[] }[] {
  return CATEGORY_ORDER.map(category => ({
    category,
    columns: Object.values(displayFragment).filter(col => col.category === category),
  })).filter(g => g.columns.length > 0);
}

export function SidebarColumns({
  view,
  tab,
  isEvidence = false,
  columnVisibilityModel,
  setColumnVisibilityModel,
}: SidebarColumnsProps) {
  const allColumns = getAllColumns({ isEvidence, tab });
  const [search, setSearch] = useState('');
  const [collapsedCategories, setCollapsedCategories] = useState<Record<string, boolean>>({});

  const displayFragment = useMemo(
    () => buildDisplayFragment(allColumns, columnVisibilityModel, view, tab, isEvidence),
    [allColumns, columnVisibilityModel, view, tab, isEvidence],
  );

  const groups = useMemo(() => groupByCategory(displayFragment), [displayFragment]);

  const filteredGroups = useMemo(() => {
    if (!search.trim()) return groups;
    const q = search.trim().toLowerCase();
    return groups
      .map(g => ({
        ...g,
        columns: g.columns.filter(
          c => c.key.toLowerCase().includes(q) || (c.header && String(c.header).toLowerCase().includes(q)),
        ),
      }))
      .filter(g => g.columns.length > 0);
  }, [groups, search]);

  const handleToggle = (key: string) => {
    setColumnVisibilityModel(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleCategoryCollapsed = (category: string) => {
    setCollapsedCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  const handleToggleCategory = (category: string) => {
    const keysInCategory = groups.find(g => g.category === category)?.columns.map(c => c.key) ?? [];
    const allSelected = keysInCategory.every(k => columnVisibilityModel[k]);
    setColumnVisibilityModel(prev => {
      const next = { ...prev };
      keysInCategory.forEach(k => {
        next[k] = !allSelected;
      });
      return next;
    });
  };

  const allKeys = useMemo(() => groups.flatMap(g => g.columns.map(c => c.key)), [groups]);
  const allGlobalSelected = allKeys.length > 0 && allKeys.every(k => columnVisibilityModel[k]);

  const handleToggleAll = () => {
    setColumnVisibilityModel(prev => {
      const next = { ...prev };
      allKeys.forEach(k => {
        next[k] = !allGlobalSelected;
      });
      return next;
    });
  };

  return (
    <div className="flex flex-col h-full min-h-0 min-w-0 overflow-hidden">
      <div className="flex-shrink-0 px-3 py-2 space-y-2">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-2.5 top-1/4 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Filter columns..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
          />
        </div>
        <div className="flex items-center justify-between border-b border-gray-100 pb-2">
          <span className="text-xs text-gray-500">
            {allKeys.filter(k => columnVisibilityModel[k]).length} / {allKeys.length} selected
          </span>
          <button
            type="button"
            onClick={handleToggleAll}
            className="text-xs font-medium text-blue-600 hover:text-blue-800 focus:outline-none"
          >
            {allGlobalSelected ? 'Deselect all' : 'Select all'}
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto min-h-0">
        {filteredGroups.length === 0 ? (
          <p className="px-3 py-2 text-sm text-gray-500">No columns match.</p>
        ) : (
          filteredGroups.map(({ category, columns }) => {
            const allSelected = columns.every(c => columnVisibilityModel[c.key]);
            const isCollapsed = collapsedCategories[category] ?? false;
            return (
              <div key={category} className="pb-2">
                <button
                  type="button"
                  onClick={() => toggleCategoryCollapsed(category)}
                  className="flex items-center justify-between w-full px-3 py-1.5 sticky top-0 bg-white border-b border-gray-100 z-10 hover:bg-gray-50 text-left"
                >
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider flex items-center gap-1">
                    {isCollapsed ? (
                      <ChevronRightIcon className="w-3.5 h-3.5 flex-shrink-0" />
                    ) : (
                      <ChevronDownIcon className="w-3.5 h-3.5 flex-shrink-0" />
                    )}
                    {category}
                  </span>
                  {!isCollapsed && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={e => {
                        e.stopPropagation();
                        handleToggleCategory(category);
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          handleToggleCategory(category);
                        }
                      }}
                      className="text-xs font-medium text-blue-600 hover:text-blue-800"
                    >
                      {allSelected ? 'Hide all' : 'Show all'}
                    </span>
                  )}
                </button>
                {!isCollapsed && (
                  <ul className="space-y-0.5 pt-1">
                    {columns.map(col => (
                      <li key={col.key}>
                        <label className="flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-gray-100 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!columnVisibilityModel[col.key]}
                            onChange={() => handleToggle(col.key)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-800 truncate">{col.header}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
